import { useState } from 'react';
import ShopifyLogo from '../../icons/ShopifyLogo';

function SEOFoundation({ onComplete }) {
  const [formData, setFormData] = useState({
    keywords: {
      industry: [],
      informational: [],
      commercial: [],
      transactional: []
    },
    competitors: []
  });

  const [newKeyword, setNewKeyword] = useState({
    industry: '',
    informational: '',
    commercial: '',
    transactional: ''
  });

  const [newCompetitor, setNewCompetitor] = useState('');

  const handleAddKeyword = (type) => {
    if (newKeyword[type].trim()) {
      setFormData(prev => ({
        ...prev,
        keywords: {
          ...prev.keywords,
          [type]: [...prev.keywords[type], newKeyword[type].trim()]
        }
      }));
      setNewKeyword(prev => ({ ...prev, [type]: '' }));
    }
  };

  const handleRemoveKeyword = (type, index) => {
    setFormData(prev => ({
      ...prev,
      keywords: {
        ...prev.keywords,
        [type]: prev.keywords[type].filter((_, i) => i !== index)
      }
    }));
  };

  const handleAddCompetitor = () => {
    if (newCompetitor.trim()) {
      setFormData(prev => ({
        ...prev,
        competitors: [...prev.competitors, newCompetitor.trim()]
      }));
      setNewCompetitor('');
    }
  };

  const handleRemoveCompetitor = (index) => {
    setFormData(prev => ({
      ...prev,
      competitors: prev.competitors.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onComplete(true);
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex items-center space-x-2 mb-6">
        <span className="text-blue-500">🔍</span>
        <h2 className="text-lg font-medium">SEO Foundation</h2>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Top keywords/phrases that you believe potential customers might use when searching for your products on search engines.
      </p>

      <div className="mb-8">
        <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
          <ShopifyLogo className="h-6 w-6" />
          <p className="text-sm text-gray-600">This feature works better with Shopify connected!</p>
          <button className="ml-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Connect Shopify
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Rest of the form remains the same */}
        {/* Industry seed keywords */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industry seed keywords
          </label>
          <p className="text-sm text-gray-500 mb-4">
            Seed keywords are the foundation of your keyword research. They are typically one or two words long and represent broad topics or categories that are highly relevant to your business and are used as starting points to identify longer, more specific keywords.
          </p>
          <div className="space-y-4">
            <div className="flex">
              <input
                type="text"
                value={newKeyword.industry}
                onChange={(e) => setNewKeyword(prev => ({ ...prev, industry: e.target.value }))}
                placeholder="Add new keyword"
                className="flex-1 rounded-l-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <button
                type="button"
                onClick={() => handleAddKeyword('industry')}
                className="px-4 py-2 bg-[#0049ac] text-white rounded-r-lg hover:bg-[#0049ac]/90"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.keywords.industry.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword('industry', index)}
                    className="ml-2 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-blue-200"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Other keyword sections */}
        {['informational', 'commercial', 'transactional'].map((type) => (
          <div key={type}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {type.charAt(0).toUpperCase() + type.slice(1)} Intent
            </label>
            <div className="space-y-4">
              <div className="flex">
                <input
                  type="text"
                  value={newKeyword[type]}
                  onChange={(e) => setNewKeyword(prev => ({ ...prev, [type]: e.target.value }))}
                  placeholder="Add new keyword"
                  className="flex-1 rounded-l-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => handleAddKeyword(type)}
                  className="px-4 py-2 bg-[#0049ac] text-white rounded-r-lg hover:bg-[#0049ac]/90"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.keywords[type].map((keyword, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(type, index)}
                      className="ml-2 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-blue-200"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Competitors */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Competitors
          </label>
          <div className="space-y-4">
            <div className="flex">
              <input
                type="text"
                value={newCompetitor}
                onChange={(e) => setNewCompetitor(e.target.value)}
                placeholder="Search by name"
                className="flex-1 rounded-l-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <button
                type="button"
                onClick={handleAddCompetitor}
                className="px-4 py-2 bg-[#0049ac] text-white rounded-r-lg hover:bg-[#0049ac]/90"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.competitors.map((competitor, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                >
                  {competitor}
                  <button
                    type="button"
                    onClick={() => handleRemoveCompetitor(index)}
                    className="ml-2 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-purple-200"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 bg-[#0049ac] text-white rounded-lg hover:bg-[#0049ac]/90"
          >
            Save & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

export default SEOFoundation;